import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';

/**
 * Association labels (stile HubSpot) org-definite, assegnabili alle relazioni
 * Contatti/Aziende. CRUD sulla tabella contact_relationship_labels.
 * Vedi MD/architecture/contatti-aziende-firstclass-migrazione.md
 */

export interface RelationshipLabel {
  id: string;
  organization_id: string;
  name: string;
  color: string;
}

const DEFAULT_COLOR = '#6B7280';

interface RelationshipLabelsStore {
  labels: RelationshipLabel[];
  loading: boolean;
  fetchLabels: () => Promise<RelationshipLabel[]>;
  createLabel: (name: string, color?: string) => Promise<RelationshipLabel | null>;
  deleteLabel: (id: string) => Promise<boolean>;
}

export const useRelationshipLabelsStore = create<RelationshipLabelsStore>((set, get) => ({
  labels: [],
  loading: false,

  fetchLabels: async () => {
    set({ loading: true });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('contact_relationship_labels')
        .select('id, organization_id, name, color')
        .eq('organization_id', orgId)
        .order('name');
      if (error) throw error;
      const labels = (data as RelationshipLabel[]) || [];
      set({ labels });
      return labels;
    } catch {
      return [];
    } finally {
      set({ loading: false });
    }
  },

  createLabel: async (name, color = DEFAULT_COLOR) => {
    const clean = name.trim();
    if (!clean) return null;
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return null;
      // riusa se esiste già (case-insensitive) — la unique index lo impone comunque
      const existing = get().labels.find((l) => l.name.toLowerCase() === clean.toLowerCase());
      if (existing) return existing;
      const { data, error } = await supabase
        .from('contact_relationship_labels')
        .insert({ organization_id: orgId, name: clean, color })
        .select('id, organization_id, name, color')
        .single();
      if (error) throw error;
      const label = data as RelationshipLabel;
      set({ labels: [...get().labels, label].sort((a, b) => a.name.localeCompare(b.name)) });
      return label;
    } catch {
      return null;
    }
  },

  deleteLabel: async (id) => {
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return false;
      const { error } = await supabase
        .from('contact_relationship_labels')
        .delete()
        .eq('id', id)
        .eq('organization_id', orgId);
      if (error) throw error;
      set({ labels: get().labels.filter((l) => l.id !== id) });
      return true;
    } catch {
      return false;
    }
  },
}));
